import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import { ChevronDown, Globe } from 'lucide-react';
import { FC, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getBrowserLanguage, useLanguageState } from '@/state';

import { i18n } from '../../../utils/i18n';
import { SUPPORTED_LOCALES, type SupportedLocale } from '../../../utils/locale';
import { AuthFooter, LanguageLabel, LanguageSelect } from '../auth.styled';

/* Nazwy własne języków, każda w swoim języku: przed zalogowaniem nie wiadomo jeszcze,
   który wybierze użytkownik. Lista musi pokrywać się z językami panelu. */
const LANGUAGE_LABELS: Record<SupportedLocale, string> = {
  pl: 'Polski',
  en: 'English',
  de: 'Deutsch',
};

const AVAILABLE_LANGUAGES = SUPPORTED_LOCALES.map((code) => ({ code, label: LANGUAGE_LABELS[code] }));

export const AuthFooterBar: FC = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguageState();

  // Initialize language from browser on first mount (synchronous before paint)
  useLayoutEffect(() => {
    // Check if this is truly the first time (no language in storage at all)
    const stored = localStorage.getItem('language-storage');
    if (!stored) {
      const browserLang = getBrowserLanguage();
      setLanguage(browserLang);
      i18n.changeLanguage(browserLang);
    } else if (language && language !== i18n.language) {
      // Just sync existing language to i18n
      i18n.changeLanguage(language);
    }
  }, []); // Run only once on mount

  const currentLanguage = language || i18n.language;
  const currentLabel = AVAILABLE_LANGUAGES.find((l) => l.code === currentLanguage)?.label || 'English';

  const handleLanguageChange = (key: string) => {
    i18n.changeLanguage(key);
    setLanguage(key);
    localStorage.setItem('preAuthLanguage', key);
  };

  return (
    <AuthFooter>
      <LanguageLabel>{t('userProfile.language')}</LanguageLabel>
      <Dropdown
        classNames={{
          content: 'min-w-[120px] p-1 border border-gray-200 dark:border-[#374151] dark:bg-[#1a1f26] rounded-md shadow-sm',
        }}
      >
        <DropdownTrigger>
          <LanguageSelect type="button">
            <Globe size={14} />
            <span>{currentLabel}</span>
            <ChevronDown size={12} />
          </LanguageSelect>
        </DropdownTrigger>
        <DropdownMenu
          aria-label={t('userProfile.selectLanguage')}
          onAction={(key) => handleLanguageChange(key as string)}
          selectedKeys={new Set([currentLanguage])}
          selectionMode="single"
          classNames={{
            base: 'text-sm',
          }}
        >
          {AVAILABLE_LANGUAGES.map((lang) => (
            <DropdownItem
              key={lang.code}
              className="text-sm dark:hover:bg-[#374151]"
            >
              {lang.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </AuthFooter>
  );
};
